// TicketsView — customer-service tickets as a TABLE (Lissette 2026-06-23).
// Columns: #, subject, client (clickable → profile), client email, type,
// priority, status, "Atiende"/assignee, updated. Statuses and request types are
// agency-customizable with colors. New tickets auto-assign to the seat that
// handles the client ("Atiende"). The detail modal carries the conversation
// thread, a "how it was solved" (resolution) field and a free notes field.
//
// CRUD via explicit async props (onSaveTicket / onAddMessage / onDeleteTicket);
// status/type customization via onCustomStatusesChange / onCustomTypesChange
// (persisted into AgencySetting by the App).

import { useState, useMemo, useEffect, useRef } from 'react';
import { Ticket, Plus, Trash2, Send, MessageSquare, ExternalLink, Settings, X, Mail, Paperclip, Link2 } from 'lucide-react';
import { Modal, ClientSearchSelect, Combobox, confirmDialog } from './ui.jsx';
import { seatLabelFor } from './AttendedBy.jsx';
import { VarChips, useVarInserter } from './VarChips.jsx';
import { AiDraftButton } from './AiDraftButton.jsx';
import { canDelete } from '../seatPermissions.js';
import { toast } from './Toast.jsx';
// El menu del @ (Lissette 2026-09-13: que las menciones tambien funcionen aqui).
import { useMenciones } from './MenuDeMenciones.jsx';
// Fotos y PDF en el hilo (Lissette 2026-09-29: "poder colocar pdf o fotos").
// Las MISMAS piezas que las notas de Tareas: que se admite, como se sube y
// como se ve un adjunto viven en un solo sitio.
import { ACEPTA_NOTA, archivoAdmitido, motivoRechazo } from '../adjuntosNota.js';
import { MiniaturaAdjunto, AdjuntoGuardado } from './Adjunto.jsx';
import { uploadNoteAttachment, listDeletedTickets, restoreTicket } from '../api.js';
import PapeleraModal from './PapeleraModal.jsx';

// Phase 3 default client-email templates (mirror the backend EMAIL_DEFAULTS so
// the customize editor + the send modal prefill the same wording).
//
// Ya no se despiden con {agente}/{agencia}: el correo de tickets pasa por la
// presentacion central, que engancha la firma del perfil del agente al final
// (2026-08-20). Firmar tambien aqui imprimia el nombre dos veces. Si cambias
// esto, cambia TAMBIEN EMAIL_DEFAULTS en deskte-api/src/routes/tickets.routes.ts
// — son dos copias del mismo texto y el servidor es el que manda.
export const TICKET_EMAIL_DEFAULTS = {
  open: {
    subjectEs: 'Hemos recibido tu solicitud (#{numero})',
    subjectEn: 'We received your request (#{numero})',
    bodyEs: 'Hola {cliente},\n\nRecibimos tu solicitud y nuestro equipo ya está trabajando en ella. Tu número de caso es #{numero}.\n\nTe contactaremos pronto.\n\nGracias,',
    bodyEn: "Hi {cliente},\n\nWe've received your request and our team is already on it. Your case number is #{numero}.\n\nWe'll be in touch soon.\n\nThank you,",
  },
  resolve: {
    subjectEs: 'Tu solicitud #{numero} ha sido resuelta',
    subjectEn: 'Your request #{numero} has been resolved',
    bodyEs: 'Hola {cliente},\n\nTu solicitud "{asunto}" (#{numero}) ha sido resuelta.\n\n{resolucion}\n\nSi necesitas algo más, responde a este correo.\n\nGracias,',
    bodyEn: 'Hi {cliente},\n\nYour request "{asunto}" (#{numero}) has been resolved.\n\n{resolucion}\n\nIf you need anything else, just reply to this email.\n\nThank you,',
  },
};
// Short SMS variants for the same two notifications (Lissette 2026-07-29). Kept
// brief (SMS bills per 160 chars) and PHI-free by default — no {resolucion}.
// Stored alongside the email templates under smsEnabled/smsEs/smsEn.
export const TICKET_SMS_DEFAULTS = {
  open: {
    es: 'Hola {cliente}, recibimos tu solicitud #{numero} y ya la estamos atendiendo. Te contactamos pronto. — {agencia}',
    en: "Hi {cliente}, we received your request #{numero} and are already on it. We'll be in touch soon. — {agencia}",
  },
  resolve: {
    es: 'Hola {cliente}, tu solicitud #{numero} quedó resuelta. Si necesitas algo más, respóndenos. — {agencia}',
    en: 'Hi {cliente}, your request #{numero} has been resolved. Reply if you need anything else. — {agencia}',
  },
};
// Human-friendly ticket code: first 3 letters of the agency name + the
// per-agency sequence number zero-padded to 4 digits (e.g. "DAL0001").
// Lissette 2026-06-24. The numeric `number` stays the source of truth/sequence;
// this is display-only.
export function ticketCode(number, agencyName) {
  const pfx = String(agencyName || '').replace(/[^A-Za-z]/g, '').slice(0, 3).toUpperCase();
  return `${pfx}${String(number || 0).padStart(4, '0')}`;
}

// Client-side variable fill for the preview/prefill. The backend re-fills
// {agente}/{agencia} authoritatively at send time, so leaving those is safe.
function fillVars(tpl, t, clientName, agencyName) {
  return String(tpl || '')
    .replace(/\{cliente\}/g, clientName || '')
    .replace(/\{numero\}/g, ticketCode(t?.number, agencyName))
    .replace(/\{asunto\}/g, t?.subject || '')
    .replace(/\{tipo\}/g, t?.category || '')
    .replace(/\{resolucion\}/g, t?.resolution || '');
}

const PRIORITY = ['low', 'medium', 'high', 'urgent'];
const PRIORITY_META = {
  low: { es: 'Baja', en: 'Low', color: '#64748b' },
  medium: { es: 'Media', en: 'Medium', color: '#00d4ff' },
  high: { es: 'Alta', en: 'High', color: '#ffb700' },
  urgent: { es: 'Urgente', en: 'Urgent', color: '#ff3d6b' },
};
const BUILTIN_STATUSES = [
  { id: 'open', es: 'Abierto', en: 'Open', color: '#ffb700' },
  { id: 'in_progress', es: 'En proceso', en: 'In progress', color: '#00d4ff' },
  { id: 'waiting', es: 'En espera', en: 'Waiting', color: '#64748b' },
  { id: 'resolved', es: 'Resuelto', en: 'Resolved', color: '#00ff88' },
  { id: 'closed', es: 'Cerrado', en: 'Closed', color: '#64748b' },
];
const DEFAULT_TYPES = [
  { es: 'Cambio de póliza', en: 'Policy change' },
  { es: 'Cobros / Pagos', en: 'Billing / Payments' },
  { es: 'Reclamos', en: 'Claims' },
  { es: 'Documentos', en: 'Documents' },
  { es: 'Cancelación', en: 'Cancellation' },
  { es: 'Renovación', en: 'Renewal' },
  { es: 'General', en: 'General' },
];
const TERMINAL = ['resolved', 'closed'];
const isOpen = (t) => t && !TERMINAL.includes(t.status);
export const countOpenTickets = (arr) => (arr || []).filter(isOpen).length;
const pLabel = (p, es) => { const m = PRIORITY_META[p]; return m ? (es ? m.es : m.en) : p; };
const pColor = (p) => PRIORITY_META[p]?.color || '#64748b';

const fmtDateTime = (iso, es) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  return d.toLocaleString(es ? 'es-US' : 'en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
};
const userName = (users, id) => { const u = (users || []).find((x) => x.id === id); return u ? (u.name || u.displayName || '') : ''; };
const rid = (pfx) => pfx + Math.random().toString(36).slice(2, 8);

export function TicketsView({
  tickets = [], onSaveTicket, onAddMessage, onEditMessage, onDeleteMessage, onDeleteTicket,
  clients = [], users = [], currentUser, lang, canEdit = true,
  clientId = null, embedded = false,
  seatLabels = {}, customStatuses = [], customTypes = [],
  onCustomStatusesChange, onCustomTypesChange, onOpenClient,
  // Phase 3 (client emails): notify handler + doc loader + editable templates.
  onNotifyClient, loadClientDocs,
  ticketEmailOnOpen = {}, ticketEmailOnResolve = {},
  onEmailOnOpenChange, onEmailOnResolveChange,
  // Papelera (2026-10-15): el ticket devuelto vuelve a la lista de atras.
  onTicketRestored,
}) {
  const es = lang === 'es';
  const [showTrash, setShowTrash] = useState(false);
  const agencyName = currentUser?.agency?.name || currentUser?.companyName || '';
  const tcode = (n) => ticketCode(n, agencyName);
  const [statusF, setStatusF] = useState('open');
  const [priorityF, setPriorityF] = useState('all');
  const [assigneeF, setAssigneeF] = useState('all');
  const [q, setQ] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [openId, setOpenId] = useState(null);
  const [draftMsg, setDraftMsg] = useState('');
  // Adjuntos del PROXIMO mensaje, ya subidos: [{ name, type, size, blobUrl, _preview }].
  const [draftAdj, setDraftAdj] = useState([]);
  const [subiendoAdj, setSubiendoAdj] = useState(false);
  const adjRef = useRef(null);
  const MAX_ADJ = 10; // el mismo tope que el servidor (MAX_ADJUNTOS_MENSAJE)
  const agregarAdjuntos = async (files) => {
    const buenos = [...files].filter((f) => archivoAdmitido(f));
    [...files].filter((f) => f && !archivoAdmitido(f)).forEach((f) => toast.error(motivoRechazo(f, es)));
    if (!buenos.length) return;
    if (draftAdj.length + buenos.length > MAX_ADJ) { toast.error(es ? `Máximo ${MAX_ADJ} archivos por mensaje.` : `Max ${MAX_ADJ} files per message.`); return; }
    setSubiendoAdj(true);
    try {
      for (const f of buenos) {
        const ref = await uploadNoteAttachment(f);
        setDraftAdj((prev) => [...prev, { ...ref, name: f.name || ref.name, _preview: URL.createObjectURL(f) }]);
      }
    } catch (e) { toast.error((es ? 'No se pudo subir el archivo: ' : 'Upload failed: ') + (e.message || e)); }
    finally { setSubiendoAdj(false); }
  };
  // LA RESPUESTA DEL TICKET TAMBIEN NOMBRA. El servidor ya avisaba a quien se
  // nombra aqui (`tickets.routes.ts` llama a `notificarMenciones`), pero esta
  // caja es un `textarea` pelado y nunca tuvo el desplegable: habia que
  // acertar el nombre completo de memoria. Sin lista de `usuarios`: el gancho
  // coge la que publica deskte-full (`equipoParaMenciones.js`).
  const men = useMenciones({ onValor: setDraftMsg });
  const [busy, setBusy] = useState(false);
  const [showCustomize, setShowCustomize] = useState(false);
  const [notifyFor, setNotifyFor] = useState(null); // { ticket, kind:'resolve'|'manual' }
  // Delete gate: only owner/agent or a seat granted "records.delete" may delete.
  const allowDelete = canEdit && canDelete(currentUser);
  const [selected, setSelected] = useState(() => new Set());
  const blank = () => ({ subject: '', description: '', category: '', priority: 'medium', status: 'open', clientId: clientId || '', assigneeId: clientId ? (clients.find((c) => c.id === clientId)?.attendedBySeatId || '') : '' });
  const [form, setForm] = useState(blank);

  const statusList = useMemo(() => [
    ...BUILTIN_STATUSES.map((s) => ({ id: s.id, label: es ? s.es : s.en, color: s.color, builtin: true })),
    ...(customStatuses || []).filter((s) => s && s.id && s.label).map((s) => ({ id: s.id, label: s.label, color: s.color || '#64748b' })),
  ], [customStatuses, es]);
  const statusInfo = (id) => statusList.find((s) => s.id === id) || { id, label: id, color: '#64748b' };

  const typeOptions = useMemo(() => {
    const seen = new Set(); const out = [];
    DEFAULT_TYPES.forEach((c) => { const v = es ? c.es : c.en; if (!seen.has(v)) { seen.add(v); out.push(v); } });
    (customTypes || []).forEach((t) => { const v = typeof t === 'string' ? t : t.label; if (v && !seen.has(v)) { seen.add(v); out.push(v); } });
    (tickets || []).forEach((t) => { if (t.category && !seen.has(t.category)) { seen.add(t.category); out.push(t.category); } });
    return out;
  }, [customTypes, tickets, es]);

  const scoped = useMemo(() => (clientId ? (tickets || []).filter((t) => t.clientId === clientId) : (tickets || [])), [tickets, clientId]);
  const clientEmail = (cid) => { const c = clients.find((x) => x.id === cid); return c ? (c.email || '') : ''; };

  const filtered = useMemo(() => {
    let rows = scoped;
    if (statusF === 'open') rows = rows.filter(isOpen);
    else if (statusF !== 'all') rows = rows.filter((t) => t.status === statusF);
    if (priorityF !== 'all') rows = rows.filter((t) => t.priority === priorityF);
    if (assigneeF === 'mine') rows = rows.filter((t) => t.assigneeId === currentUser?.id);
    else if (assigneeF === 'unassigned') rows = rows.filter((t) => !t.assigneeId);
    else if (assigneeF !== 'all') rows = rows.filter((t) => t.assigneeId === assigneeF);
    const term = q.trim().toLowerCase();
    if (term) rows = rows.filter((t) => [t.subject, t.description, t.clientName, t.category, '#' + t.number, tcode(t.number), clientEmail(t.clientId)].filter(Boolean).join(' ').toLowerCase().includes(term));
    const prioRank = { urgent: 0, high: 1, medium: 2, low: 3 };
    return rows.slice().sort((a, b) => {
      const oa = isOpen(a) ? 0 : 1, ob = isOpen(b) ? 0 : 1;
      if (oa !== ob) return oa - ob;
      if (oa === 0) { const pr = (prioRank[a.priority] ?? 2) - (prioRank[b.priority] ?? 2); if (pr) return pr; }
      return String(b.updatedAt || '').localeCompare(String(a.updatedAt || ''));
    });
  }, [scoped, statusF, priorityF, assigneeF, q, currentUser, clients]);

  const detail = openId ? (tickets || []).find((t) => t.id === openId) : null;
  const goClient = (cid) => {
    if (!cid) return;
    if (onOpenClient) { onOpenClient(cid); return; }
    // Global nav pattern: stash the client object + flip the view to "clients".
    const cl = clients.find((c) => c.id === cid);
    if (cl && typeof window !== 'undefined' && window.__magSetView) { window.__magGoToClient = cl; window.__magSetView('clients'); }
  };

  const openNew = () => { setForm(blank()); setShowForm(true); };
  const onPickClient = (cid) => setForm((f) => ({ ...f, clientId: cid, assigneeId: f.assigneeId || (clients.find((c) => c.id === cid)?.attendedBySeatId || '') }));

  const submitNew = async () => {
    if (!form.subject.trim()) { toast.error(es ? 'Escribe el asunto' : 'Enter a subject'); return; }
    const subject = clients.find((c) => c.id === form.clientId);
    setBusy(true);
    try {
      const saved = await onSaveTicket({
        subject: form.subject, description: form.description, category: form.category,
        priority: form.priority, status: form.status,
        clientId: form.clientId || '', clientName: subject ? `${subject.firstName || ''} ${subject.lastName || ''}`.trim() : '',
        assigneeId: form.assigneeId || '',
      });
      toast.success(es ? 'Ticket creado ✓' : 'Ticket created ✓');
      setShowForm(false);
      if (saved?.id) setOpenId(saved.id);
    } catch (e) { toast.error((es ? 'No se pudo crear: ' : 'Failed to create: ') + e.message); }
    finally { setBusy(false); }
  };

  const patch = async (t, p) => { try { await onSaveTicket({ ...t, ...p }); } catch (e) { toast.error((es ? 'No se pudo guardar: ' : 'Failed to save: ') + e.message); } };
  // Corregir o retirar un mensaje propio del hilo (Francisco Picado, Pomares
  // Insurance Group, 2026-08-25: "a veces uno se suele confundir y quiere
  // editar la nota para corregir o agregar informacion faltante").
  //
  // El texto original NO se pierde: el servidor lo guarda en `originalBody`.
  // Un ticket puede acabar siendo la prueba de que se aviso a un cliente de
  // algo, asi que editar corrige a la vista dejando ver que se corrigio.
  const [editandoMsg, setEditandoMsg] = useState(null); // {id, texto}
  const guardarMsg = async () => {
    const texto = (editandoMsg?.texto || '').trim();
    if (!texto || !detail || busy) return;
    setBusy(true);
    try { await onEditMessage(detail.id, editandoMsg.id, texto); setEditandoMsg(null); }
    catch (e) { toast.error((es ? 'No se pudo corregir: ' : 'Could not edit: ') + e.message); }
    finally { setBusy(false); }
  };
  const retirarMsg = async (msgId) => {
    if (!detail) return;
    // Se avisa del rastro: "eliminar" aqui no significa lo mismo que en otras
    // pantallas, y eso no puede ser una sorpresa.
    if (!window.confirm(es
      ? 'El mensaje dejara de verse, pero quedara marcado como eliminado en el hilo. ¿Continuar?'
      : 'The message will stop showing, but it stays marked as deleted in the thread. Continue?')) return;
    setBusy(true);
    try { await onDeleteMessage(detail.id, msgId); }
    catch (e) { toast.error((es ? 'No se pudo eliminar: ' : 'Could not delete: ') + e.message); }
    finally { setBusy(false); }
  };

  const sendMsg = async () => {
    const body = draftMsg.trim();
    if ((!body && !draftAdj.length) || !detail || subiendoAdj) return;
    // Al servidor solo va la referencia; la vista previa local se queda aqui.
    const adjuntos = draftAdj.map(({ name, type, size, blobUrl }) => ({ name, type, size, blobUrl }));
    setBusy(true);
    try { await onAddMessage(detail.id, body, undefined, adjuntos); setDraftMsg(''); setDraftAdj([]); }
    catch (e) { toast.error((es ? 'No se pudo enviar: ' : 'Failed to send: ') + e.message); }
    finally { setBusy(false); }
  };
  const remove = async (t) => {
    const ok = await confirmDialog({ title: es ? 'Eliminar ticket' : 'Delete ticket', message: es ? `¿Eliminar el ticket ${tcode(t.number)}?` : `Delete ticket ${tcode(t.number)}?`, danger: true, confirmLabel: es ? 'Eliminar' : 'Delete' });
    if (!ok) return;
    try { await onDeleteTicket(t.id); if (openId === t.id) setOpenId(null); } catch (e) { toast.error((es ? 'No se pudo eliminar: ' : 'Failed to delete: ') + e.message); }
  };
  // ── Multi-select for bulk delete ──────────────────────────────────────
  const toggleSelect = (id) => setSelected((prev) => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const toggleSelectAll = (checked) => setSelected((prev) => { const n = new Set(prev); filtered.forEach((t) => { if (checked) n.add(t.id); else n.delete(t.id); }); return n; });
  const clearSelection = () => setSelected(new Set());
  useEffect(() => { setSelected((prev) => { if (prev.size === 0) return prev; const ids = new Set(filtered.map((t) => t.id)); const n = new Set([...prev].filter((id) => ids.has(id))); return n.size === prev.size ? prev : n; }); }, [filtered]);
  const allChecked = filtered.length > 0 && filtered.every((t) => selected.has(t.id));
  const bulkDelete = async () => {
    const ids = [...selected]; if (ids.length === 0) return;
    const ok = await confirmDialog({ title: es ? 'Eliminar tickets' : 'Delete tickets', message: es ? `¿Estás seguro de borrar ${ids.length} ${ids.length === 1 ? 'ticket' : 'tickets'}? Se podrán devolver desde la Papelera.` : `Are you sure you want to delete ${ids.length} ${ids.length === 1 ? 'ticket' : 'tickets'}? They can be restored from the Trash.`, danger: true, confirmLabel: es ? 'Eliminar' : 'Delete' });
    if (!ok) return;
    for (const id of ids) { try { await onDeleteTicket(id); if (openId === id) setOpenId(null); } catch (e) { toast.error((es ? 'No se pudo eliminar: ' : 'Failed to delete: ') + e.message); } }
    clearSelection();
  };
  // Changing the status patches it, then — when crossing into Resolved/Closed and
  // the agency enabled the resolution email — pops the confirm-before-send modal.
  const canEmail = (cid) => !!(cid && clientEmail(cid) && onNotifyClient);
  const clientPhone = (cid) => { const c = clients.find((x) => x.id === cid); return c ? (c.phone || '') : ''; };
  const canSms = (cid) => !!(cid && clientPhone(cid) && onNotifyClient);
  const onChangeStatus = async (t, newStatus) => {
    await patch(t, { status: newStatus });
    const crossingToDone = TERMINAL.includes(newStatus) && !TERMINAL.includes(t.status);
    // Pop the confirm-before-send modal when EITHER channel is enabled for
    // resolution and the client is reachable on that channel.
    const emailOn = ticketEmailOnResolve?.enabled && canEmail(t.clientId);
    const smsOn = ticketEmailOnResolve?.smsEnabled && canSms(t.clientId);
    if (crossingToDone && (emailOn || smsOn)) {
      setNotifyFor({ ticket: { ...t, status: newStatus }, kind: 'resolve' });
    }
  };

  const StatusPill = ({ s }) => { const i = statusInfo(s); return <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '.12rem .5rem', borderRadius: 100, fontSize: '.64rem', fontWeight: 700, fontFamily: 'var(--font-mono)', background: `${i.color}22`, color: i.color, border: `1px solid ${i.color}55` }}><span style={{ width: 6, height: 6, borderRadius: '50%', background: i.color }} />{i.label}</span>; };
  const PrioPill = ({ p }) => { const c = pColor(p); return <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '.12rem .5rem', borderRadius: 100, fontSize: '.64rem', fontWeight: 700, fontFamily: 'var(--font-mono)', background: `${c}22`, color: c, border: `1px solid ${c}55` }}>{pLabel(p, es)}</span>; };
  const attendName = (t) => { if (!t.assigneeId) return es ? 'Sin asignar' : 'Unassigned'; const l = seatLabelFor(t.assigneeId, seatLabels, users); return l ? l.name : userName(users, t.assigneeId); };

  const th = { textAlign: 'left', padding: '.45rem .6rem', fontSize: '.62rem', fontWeight: 700, color: 'var(--c-text3,#45688a)', textTransform: 'uppercase', letterSpacing: '.06em', fontFamily: 'var(--font-mono)', whiteSpace: 'nowrap', borderBottom: '1px solid var(--c-border,#1a3550)' };
  const td = { padding: '.5rem .6rem', fontSize: '.78rem', color: 'var(--c-text,#c8dff0)', borderBottom: '1px solid var(--c-border,#1a3550)', verticalAlign: 'middle' };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '1rem', flexWrap: 'wrap' }}>
        {embedded
          ? <div style={{ fontWeight: 700, fontSize: '.9rem', color: 'var(--c-white,#eef6ff)', display: 'flex', alignItems: 'center', gap: '.4rem' }}><Ticket size={15} /> {es ? 'Tickets' : 'Tickets'}</div>
          : <div><div className="dkt-sec-title">🎫 {es ? 'Tickets' : 'Tickets'}</div><div className="dkt-sec-sub">{es ? 'Casos y solicitudes de clientes' : 'Client cases and requests'}</div></div>}
        <div style={{ display: 'flex', gap: '.5rem' }}>
          {canEdit && !embedded && <button type="button" className="dkt-btn ghost" onClick={() => setShowCustomize(true)}><Settings size={14} /> {es ? 'Personalizar' : 'Customize'}</button>}
          {!embedded && onTicketRestored && <button type="button" className="dkt-btn ghost" onClick={() => setShowTrash(true)} title={es ? 'Tickets borrados que todavía se pueden devolver' : 'Deleted tickets that can still be restored'}><Trash2 size={14} /> {es ? 'Papelera' : 'Trash'}</button>}
          {canEdit && <button type="button" className="dkt-btn primary" onClick={openNew}><Plus size={15} /> {es ? 'Nuevo ticket' : 'New ticket'}</button>}
        </div>
      </div>
      {showTrash && (
        <PapeleraModal lang={lang} onClose={() => setShowTrash(false)}
          titulo={es ? 'Papelera de tickets' : 'Ticket trash'}
          vacio={es ? 'No hay tickets borrados en este período.' : 'No tickets deleted in this period.'}
          nombreDe={(r) => `${tcode(r.number)} · ${r.subject || ''}`.trim()}
          detalleDe={(r) => r.clientName || ''}
          listar={listDeletedTickets} devolver={restoreTicket}
          onRestored={onTicketRestored} />
      )}

      {/* Filters */}
      <div style={{ display: 'flex', gap: '.5rem', flexWrap: 'wrap', alignItems: 'center' }}>
        <select className="dkt-inp" style={{ width: 'auto', padding: '.38rem .6rem', fontSize: '.75rem' }} value={statusF} onChange={(e) => setStatusF(e.target.value)}>
          <option value="open">{es ? 'Abiertos' : 'Open'}</option>
          <option value="all">{es ? 'Todos' : 'All'}</option>
          {statusList.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
        </select>
        <select className="dkt-inp" style={{ width: 'auto', padding: '.38rem .6rem', fontSize: '.75rem' }} value={priorityF} onChange={(e) => setPriorityF(e.target.value)}>
          <option value="all">{es ? 'Toda prioridad' : 'Any priority'}</option>
          {PRIORITY.map((p) => <option key={p} value={p}>{pLabel(p, es)}</option>)}
        </select>
        {!embedded && (
          <select className="dkt-inp" style={{ width: 'auto', padding: '.38rem .6rem', fontSize: '.75rem' }} value={assigneeF} onChange={(e) => setAssigneeF(e.target.value)}>
            <option value="all">{es ? 'Todos los responsables' : 'Any assignee'}</option>
            <option value="mine">{es ? 'Asignados a mí' : 'Assigned to me'}</option>
            <option value="unassigned">{es ? 'Sin asignar' : 'Unassigned'}</option>
            {(users || []).map((u) => <option key={u.id} value={u.id}>{(seatLabelFor(u.id, seatLabels, users) || {}).name || u.name}</option>)}
          </select>
        )}
        {!embedded && <input className="dkt-search" style={{ flex: 1, minWidth: 160, maxWidth: 320 }} placeholder={es ? '🔍 Buscar…' : '🔍 Search…'} value={q} onChange={(e) => setQ(e.target.value)} />}
        <span style={{ fontSize: '.72rem', color: 'var(--c-text3,#45688a)', marginLeft: 'auto' }} className="dkt-mono">{scoped.filter(isOpen).length} {es ? 'abiertos' : 'open'}</span>
      </div>

      {/* Bulk action bar */}
      {allowDelete && selected.size > 0 && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '.6rem .9rem', borderRadius: 12, background: 'var(--c-red-dim,rgba(255,61,107,.08))', border: '1px solid rgba(255,61,107,.25)' }}>
          <span style={{ fontSize: '.82rem', fontWeight: 600, color: 'var(--c-text,#c8dff0)' }}>{selected.size} {es ? (selected.size === 1 ? 'seleccionado' : 'seleccionados') : 'selected'}</span>
          <button type="button" className="dkt-btn" style={{ marginLeft: 'auto', background: 'var(--c-red,#ff3d6b)', color: '#fff' }} onClick={bulkDelete}><Trash2 size={14} /> {es ? 'Eliminar seleccionados' : 'Delete selected'}</button>
          <button type="button" className="dkt-btn ghost" onClick={clearSelection}><X size={13} /> {es ? 'Cancelar' : 'Cancel'}</button>
        </div>
      )}

      {/* Table */}
      {filtered.length === 0 ? (
        <div className="dkt-card" style={{ padding: '2rem', textAlign: 'center', color: 'var(--c-text3,#45688a)', fontSize: '.85rem' }}>{es ? 'Sin tickets. Crea uno con “Nuevo ticket”.' : 'No tickets yet.'}</div>
      ) : (
        <div className="dkt-card" style={{ overflowX: 'auto', padding: 0 }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead><tr>
              {allowDelete && <th style={{ ...th, width: 34, textAlign: 'center' }}><input type="checkbox" checked={allChecked} onChange={(e) => toggleSelectAll(e.target.checked)} title={es ? 'Seleccionar todo' : 'Select all'} style={{ cursor: 'pointer', width: 15, height: 15 }} /></th>}
              <th style={th}>#</th>
              <th style={th}>{es ? 'Asunto' : 'Subject'}</th>
              {!embedded && <th style={th}>{es ? 'Cliente' : 'Client'}</th>}
              {!embedded && <th style={th}>{es ? 'Correo' : 'Email'}</th>}
              <th style={th}>{es ? 'Tipo' : 'Type'}</th>
              <th style={th}>{es ? 'Prioridad' : 'Priority'}</th>
              <th style={th}>{es ? 'Estado' : 'Status'}</th>
              <th style={th}>{es ? 'Atiende' : 'Handled by'}</th>
              <th style={th}>{es ? 'Actualizado' : 'Updated'}</th>
            </tr></thead>
            <tbody>
              {filtered.map((t) => (
                <tr key={t.id} style={{ cursor: 'pointer', opacity: isOpen(t) ? 1 : 0.65 }} onClick={() => { setOpenId(t.id); setDraftMsg(''); }}
                  onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--c-surface2,#112538)')} onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}>
                  {allowDelete && <td style={{ ...td, textAlign: 'center' }} onClick={(e) => e.stopPropagation()}><input type="checkbox" checked={selected.has(t.id)} onChange={() => toggleSelect(t.id)} style={{ cursor: 'pointer', width: 15, height: 15 }} /></td>}
                  <td style={{ ...td, fontFamily: 'var(--font-mono)', color: 'var(--c-text3,#45688a)', whiteSpace: 'nowrap' }}>{tcode(t.number)}</td>
                  <td style={{ ...td, fontWeight: 600, color: 'var(--c-white,#eef6ff)', maxWidth: 260, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {t.subject}{(t.messages || []).length > 0 && <span style={{ marginLeft: 6, color: 'var(--c-text3,#45688a)', fontSize: '.68rem' }}><MessageSquare size={10} style={{ verticalAlign: 'middle' }} /> {t.messages.length}</span>}
                  </td>
                  {!embedded && <td style={td}>{t.clientId ? <button type="button" onClick={(e) => { e.stopPropagation(); goClient(t.clientId); }} style={{ background: 'none', border: 'none', color: 'var(--c-cyan,#00d4ff)', cursor: 'pointer', padding: 0, font: 'inherit', display: 'inline-flex', alignItems: 'center', gap: 3 }}>{t.clientName || (es ? 'Ver' : 'Open')} <ExternalLink size={11} /></button> : <span style={{ color: 'var(--c-text3,#45688a)' }}>—</span>}</td>}
                  {!embedded && <td style={{ ...td, color: 'var(--c-text2,#7aa3c0)', fontSize: '.72rem' }}>{clientEmail(t.clientId) || '—'}</td>}
                  <td style={{ ...td, color: 'var(--c-text2,#7aa3c0)', fontSize: '.72rem' }}>{t.category || '—'}</td>
                  <td style={td}><PrioPill p={t.priority} /></td>
                  <td style={td}><StatusPill s={t.status} /></td>
                  <td style={{ ...td, fontSize: '.72rem', color: 'var(--c-text2,#7aa3c0)' }}>{attendName(t)}</td>
                  <td style={{ ...td, fontSize: '.68rem', color: 'var(--c-text3,#45688a)', fontFamily: 'var(--font-mono)', whiteSpace: 'nowrap' }}>{fmtDateTime(t.updatedAt, es)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* New ticket modal */}
      {showForm && (
        <Modal title={es ? 'Nuevo ticket' : 'New ticket'} onClose={() => setShowForm(false)}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '.85rem' }}>
            <div><label className="dkt-input-lbl">{es ? 'Asunto' : 'Subject'}</label><input className="dkt-inp" value={form.subject} onChange={(e) => setForm((f) => ({ ...f, subject: e.target.value }))} autoFocus /></div>
            {!clientId && <div><label className="dkt-input-lbl">{es ? 'Cliente' : 'Client'}</label><ClientSearchSelect clients={clients} value={form.clientId} onChange={(e) => onPickClient(e.target.value)} lang={lang} /></div>}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '.6rem' }}>
              <div><label className="dkt-input-lbl">{es ? 'Tipo de solicitud' : 'Request type'}</label><Combobox value={form.category} onChange={(v) => setForm((f) => ({ ...f, category: v }))} options={typeOptions} lang={lang} placeholder={es ? 'Elige o escribe…' : 'Pick or type…'} /></div>
              <div><label className="dkt-input-lbl">{es ? 'Prioridad' : 'Priority'}</label><select className="dkt-inp" value={form.priority} onChange={(e) => setForm((f) => ({ ...f, priority: e.target.value }))}>{PRIORITY.map((p) => <option key={p} value={p}>{pLabel(p, es)}</option>)}</select></div>
            </div>
            <div><label className="dkt-input-lbl">{es ? 'Atiende / Asignar a' : 'Assign to'}{form.assigneeId && form.clientId && clients.find((c) => c.id === form.clientId)?.attendedBySeatId === form.assigneeId ? ` · ${es ? 'auto del cliente' : 'auto from client'}` : ''}</label><select className="dkt-inp" value={form.assigneeId} onChange={(e) => setForm((f) => ({ ...f, assigneeId: e.target.value }))}><option value="">{es ? '— Sin asignar —' : '— Unassigned —'}</option>{(users || []).map((u) => <option key={u.id} value={u.id}>{(seatLabelFor(u.id, seatLabels, users) || {}).name || u.name}</option>)}</select></div>
            <div><label className="dkt-input-lbl">{es ? 'Descripción' : 'Description'}</label><textarea className="dkt-inp" rows={4} value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} /></div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '.5rem' }}>
              <button type="button" className="dkt-btn ghost" onClick={() => setShowForm(false)}>{es ? 'Cancelar' : 'Cancel'}</button>
              <button type="button" className="dkt-btn primary" disabled={busy} onClick={submitNew}>{es ? 'Crear ticket' : 'Create ticket'}</button>
            </div>
          </div>
        </Modal>
      )}

      {/* Detail modal */}
      {detail && (
        <Modal title={`${tcode(detail.number)} · ${detail.subject}`} onClose={() => setOpenId(null)} wide>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '.9rem' }}>
            <div style={{ display: 'flex', gap: '.5rem', flexWrap: 'wrap', alignItems: 'center' }}>
              <select className="dkt-inp" style={{ width: 'auto', padding: '.4rem .6rem', fontSize: '.78rem' }} value={detail.status} onChange={(e) => onChangeStatus(detail, e.target.value)} disabled={!canEdit}>
                {statusList.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
              </select>
              <select className="dkt-inp" style={{ width: 'auto', padding: '.4rem .6rem', fontSize: '.78rem' }} value={detail.priority} onChange={(e) => patch(detail, { priority: e.target.value })} disabled={!canEdit}>
                {PRIORITY.map((p) => <option key={p} value={p}>{pLabel(p, es)}</option>)}
              </select>
              <select className="dkt-inp" style={{ width: 'auto', padding: '.4rem .6rem', fontSize: '.78rem' }} value={detail.assigneeId || ''} onChange={(e) => patch(detail, { assigneeId: e.target.value })} disabled={!canEdit}>
                <option value="">{es ? '— Sin asignar —' : '— Unassigned —'}</option>
                {(users || []).map((u) => <option key={u.id} value={u.id}>{(seatLabelFor(u.id, seatLabels, users) || {}).name || u.name}</option>)}
              </select>
              {canEdit && canEmail(detail.clientId) && (
                <button type="button" className="dkt-btn ghost" style={{ marginLeft: 'auto' }} onClick={() => setNotifyFor({ ticket: detail, kind: 'manual' })}><Mail size={14} /> {es ? 'Avisar al cliente' : 'Email client'}</button>
              )}
              {allowDelete && <button type="button" className="dkt-btn ghost" style={canEmail(detail.clientId) ? {} : { marginLeft: 'auto' }} onClick={() => remove(detail)}><Trash2 size={14} /> {es ? 'Eliminar' : 'Delete'}</button>}
            </div>

            <div style={{ display: 'flex', gap: '.5rem', flexWrap: 'wrap', fontSize: '.76rem', color: 'var(--c-text2,#7aa3c0)', alignItems: 'center' }}>
              {detail.clientId && <button type="button" onClick={() => goClient(detail.clientId)} style={{ background: 'none', border: 'none', color: 'var(--c-cyan,#00d4ff)', cursor: 'pointer', padding: 0, font: 'inherit', display: 'inline-flex', alignItems: 'center', gap: 3 }}>👤 {detail.clientName} <ExternalLink size={11} /></button>}
              {clientEmail(detail.clientId) && <span>✉️ {clientEmail(detail.clientId)}</span>}
              {detail.category && <span className="dkt-bdg dkt-bdg-dim">{detail.category}</span>}
              <span className="dkt-mono" style={{ color: 'var(--c-text3,#45688a)' }}>{es ? 'Creado ' : 'Created '}{fmtDateTime(detail.createdAt, es)}{detail.createdById ? ` · ${userName(users, detail.createdById)}` : ''}</span>
            </div>

            {detail.description && <div style={{ fontSize: '.85rem', color: 'var(--c-text,#c8dff0)', whiteSpace: 'pre-wrap', background: 'var(--c-surface2,#112538)', border: '1px solid var(--c-border,#1a3550)', borderRadius: 8, padding: '.7rem .85rem' }}>{detail.description}</div>}

            <div style={{ display: 'flex', flexDirection: 'column', gap: '.5rem' }}>
              <div className="dkt-cyan-lbl">{es ? 'Conversación' : 'Conversation'}</div>
              {(detail.messages || []).length === 0 && <div style={{ fontSize: '.78rem', color: 'var(--c-text3,#45688a)' }}>{es ? 'Sin mensajes todavía.' : 'No messages yet.'}</div>}
              {(detail.messages || []).map((m) => (
                <div key={m.id} style={{ background: 'var(--c-surface,#0d1c2e)', border: '1px solid var(--c-border,#1a3550)', borderRadius: 8, padding: '.55rem .75rem' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: '.5rem', fontSize: '.7rem', color: 'var(--c-text3,#45688a)', marginBottom: '.25rem' }} className="dkt-mono">
                    <span>{m.authorName || userName(users, m.authorId) || (es ? 'Equipo' : 'Team')}</span>
                    <span>{fmtDateTime(m.createdAt, es)}{m.editedAt && !m.deleted ? ` · ${es ? 'editado' : 'edited'}` : ''}</span>
                  </div>
                  {m.deleted ? (
                    /* No se quita del hilo: se queda marcado. Un hilo con
                       huecos deja respuestas que ya no se entienden. */
                    <div style={{ fontSize: '.78rem', color: 'var(--c-text3,#45688a)', fontStyle: 'italic' }}>
                      {es ? 'Mensaje eliminado' : 'Message deleted'}
                    </div>
                  ) : editandoMsg && editandoMsg.id === m.id ? (
                    <div>
                      <textarea className="dkt-inp" rows={3} style={{ width: '100%', boxSizing: 'border-box' }} autoFocus
                        value={editandoMsg.texto} onChange={(e) => setEditandoMsg((p) => ({ ...p, texto: e.target.value }))} />
                      <div style={{ display: 'flex', gap: '.4rem', marginTop: '.35rem' }}>
                        <button type="button" className="dkt-btn primary" style={{ padding: '.25rem .7rem', fontSize: '.72rem' }}
                          disabled={busy || !editandoMsg.texto.trim()} onClick={guardarMsg}>{es ? 'Guardar' : 'Save'}</button>
                        <button type="button" className="dkt-btn" style={{ padding: '.25rem .7rem', fontSize: '.72rem' }}
                          onClick={() => setEditandoMsg(null)}>{es ? 'Cancelar' : 'Cancel'}</button>
                      </div>
                    </div>
                  ) : (
                    <>
                      {m.body && <div style={{ fontSize: '.82rem', color: 'var(--c-text,#c8dff0)', whiteSpace: 'pre-wrap' }}>{m.body}</div>}
                      {(m.attachments || []).length > 0 && (
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: m.body ? 6 : 0 }}>
                          {m.attachments.map((a, i) => (
                            <AdjuntoGuardado key={i} url={a.url || a.blobUrl} nombre={a.name} lado={84} lang={es ? 'es' : 'en'} />
                          ))}
                        </div>
                      )}
                    </>
                  )}
                  {/* Solo el autor, y nunca los mensajes del sistema. El
                      servidor lo comprueba igualmente. */}
                  {canEdit && !m.deleted && !editandoMsg && m.authorId === currentUser?.id && m.authorId !== 'system' && (
                    <div style={{ display: 'flex', gap: '.6rem', marginTop: '.35rem' }}>
                      <button type="button" onClick={() => setEditandoMsg({ id: m.id, texto: String(m.body || '') })}
                        style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: 'var(--c-text3,#45688a)', fontSize: '.68rem', fontWeight: 700, fontFamily: 'inherit' }}>
                        {es ? 'Editar' : 'Edit'}
                      </button>
                      <button type="button" onClick={() => retirarMsg(m.id)}
                        style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: 'var(--c-text3,#45688a)', fontSize: '.68rem', fontWeight: 700, fontFamily: 'inherit' }}>
                        {es ? 'Eliminar' : 'Delete'}
                      </button>
                    </div>
                  )}
                </div>
              ))}
              {canEdit && (
                <div>
                {draftAdj.length > 0 && (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 6 }}>
                    {draftAdj.map((a, i) => (
                      <MiniaturaAdjunto key={i} adjunto={a} lado={52} lang={es ? 'es' : 'en'}
                        onQuitar={() => setDraftAdj((p) => p.filter((_, j) => j !== i))} />
                    ))}
                  </div>
                )}
                <div style={{ display: 'flex', gap: '.5rem', alignItems: 'flex-end' }}>
                  <input ref={adjRef} type="file" accept={ACEPTA_NOTA} multiple style={{ display: 'none' }}
                    onChange={(e) => { const fs = Array.from(e.target.files || []); e.target.value = ''; if (fs.length) agregarAdjuntos(fs); }} />
                  <button type="button" className="dkt-btn" disabled={busy || subiendoAdj} onClick={() => adjRef.current && adjRef.current.click()}
                    title={es ? 'Adjuntar foto o PDF' : 'Attach photo or PDF'} style={{ padding: '.45rem .55rem' }}>
                    {subiendoAdj ? '…' : <Paperclip size={14} />}
                  </button>
                  {/* Relativo para que el menu del `@` se pinte pegado a la caja. */}
                  <div style={{ position: 'relative', flex: 1 }}>
                    <textarea className="dkt-inp" rows={2} style={{ width: '100%', boxSizing: 'border-box' }} placeholder={es ? 'Escribe una nota… (puedes pegar una foto)' : 'Write a note… (you can paste a photo)'}
                      onPaste={(e) => {
                        const fs = Array.from((e.clipboardData && e.clipboardData.files) || []).filter((f) => archivoAdmitido(f));
                        if (fs.length) { e.preventDefault(); agregarAdjuntos(fs); }
                      }}
                      value={draftMsg} ref={men.ref} onChange={men.onChange} onClick={men.onClick} onBlur={men.onBlur}
                      onKeyDown={(e) => {
                        // El menu manda primero: con el abierto, Enter elige un
                        // nombre y no envia el mensaje a medio escribir.
                        men.onKeyDown(e);
                        if (e.defaultPrevented) return;
                        if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) sendMsg();
                      }} />
                    {men.panel}
                  </div>
                  <button type="button" className="dkt-btn primary" disabled={busy || subiendoAdj || (!draftMsg.trim() && !draftAdj.length)} onClick={sendMsg}><Send size={14} /> {es ? 'Enviar' : 'Send'}</button>
                </div>
                </div>
              )}
            </div>

            <div>
              <label className="dkt-input-lbl">{es ? 'Cómo se solucionó' : 'How it was solved'}</label>
              <textarea className="dkt-inp" rows={3} defaultValue={detail.resolution || ''} disabled={!canEdit} key={'res-' + detail.id}
                onBlur={(e) => { if (canEdit && e.target.value !== (detail.resolution || '')) patch(detail, { resolution: e.target.value }); }} />
            </div>
          </div>
        </Modal>
      )}

      {showCustomize && (
        <CustomizeModal lang={lang} onClose={() => setShowCustomize(false)}
          customStatuses={customStatuses} customTypes={customTypes}
          onCustomStatusesChange={onCustomStatusesChange} onCustomTypesChange={onCustomTypesChange}
          emailOnOpen={ticketEmailOnOpen} emailOnResolve={ticketEmailOnResolve}
          onEmailOnOpenChange={onEmailOnOpenChange} onEmailOnResolveChange={onEmailOnResolveChange} />
      )}

      {notifyFor && (
        <NotifyModal lang={lang} kind={notifyFor.kind} ticket={notifyFor.ticket} agencyName={agencyName}
          client={clients.find((c) => c.id === notifyFor.ticket.clientId)}
          template={notifyFor.kind === 'resolve' ? ticketEmailOnResolve : ticketEmailOnOpen}
          onNotifyClient={onNotifyClient} loadClientDocs={loadClientDocs}
          onClose={() => setNotifyFor(null)} />
      )}
    </div>
  );
}

// Confirm-before-send client email for a ticket (resolution / manual). Prefills
// from the agency template (falling back to the built-in default), lets the
// agent edit, pick the language, and attach client documents as 7-day links or
// real file attachments. The backend re-fills {agente}/{agencia} authoritatively.
function NotifyModal({ lang, kind, ticket, client, template, agencyName, onNotifyClient, loadClientDocs, onClose }) {
  const es = lang === 'es';
  const clientLangEs = !client
    ? true
    : (String(client.preferredLanguage || '').toLowerCase().startsWith('es') || client.preferredLang === 'spanish'
        || !(String(client.preferredLanguage || '').toLowerCase().startsWith('en') || client.preferredLang === 'english'));
  const clientName = client ? `${client.firstName || ''} ${client.lastName || ''}`.trim() : (ticket.clientName || '');
  const defaults = kind === 'resolve' ? TICKET_EMAIL_DEFAULTS.resolve : TICKET_EMAIL_DEFAULTS.open;
  const pick = (langEs, field) => { const t = template || {}; const key = field + (langEs ? 'Es' : 'En'); return t[key] || defaults[key]; };
  // SMS channel (Lissette 2026-07-29): offered when the agency enabled text for
  // this notification AND the client has a phone. Independent short message.
  const smsDefaults = kind === 'resolve' ? TICKET_SMS_DEFAULTS.resolve : TICKET_SMS_DEFAULTS.open;
  const pickSms = (langEs) => { const t = template || {}; return t[langEs ? 'smsEs' : 'smsEn'] || smsDefaults[langEs ? 'es' : 'en']; };
  // Email channel is available when the client has an email AND we're allowed to
  // email for this notification: the manual "Avisar al cliente" button is always
  // an email send; the resolution modal only emails when that channel is enabled
  // (otherwise it's an SMS-only confirm).
  const hasEmail = !!(client?.email) && (kind !== 'resolve' || !!template?.enabled);
  const smsAvailable = !!(template?.smsEnabled && client?.phone);

  const [emailEs, setEmailEs] = useState(clientLangEs);
  const [subject, setSubject] = useState(() => fillVars(pick(clientLangEs, 'subject'), ticket, clientName, agencyName));
  const [body, setBody] = useState(() => fillVars(pick(clientLangEs, 'body'), ticket, clientName, agencyName));
  const [alsoSms, setAlsoSms] = useState(smsAvailable);
  const [smsText, setSmsText] = useState(() => fillVars(pickSms(clientLangEs), ticket, clientName, agencyName));
  const [docs, setDocs] = useState([]);
  const [docsLoading, setDocsLoading] = useState(false);
  const [sel, setSel] = useState({}); // { docId: 'link' | 'attach' }
  const [busy, setBusy] = useState(false);

  const relang = (next) => {
    setEmailEs(next);
    setSubject(fillVars(pick(next, 'subject'), ticket, clientName, agencyName));
    setBody(fillVars(pick(next, 'body'), ticket, clientName, agencyName));
    setSmsText(fillVars(pickSms(next), ticket, clientName, agencyName));
  };

  useEffect(() => {
    if (!loadClientDocs || !ticket.clientId) return;
    let cancelled = false;
    setDocsLoading(true);
    Promise.resolve(loadClientDocs(ticket.clientId))
      .then((rows) => { if (!cancelled) setDocs(Array.isArray(rows) ? rows : []); })
      .catch(() => { if (!cancelled) setDocs([]); })
      .finally(() => { if (!cancelled) setDocsLoading(false); });
    return () => { cancelled = true; };
  }, [ticket.clientId]);

  const toggleDoc = (id) => setSel((p) => { const n = { ...p }; if (n[id]) delete n[id]; else n[id] = 'link'; return n; });
  const setMode = (id, mode) => setSel((p) => ({ ...p, [id]: mode }));

  // Attach a file straight from the agent's PC (Lissette 2026-07-28). It's
  // uploaded as a Document tied to this ticket's client, then pre-selected as a
  // real attachment — the existing /notify + resolveEmailDocs pipeline sends it
  // with no backend change. It also shows up in the client's documents.
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const attachLocalFile = async (file) => {
    if (!file) return;
    if (!ticket.clientId) { toast.error(es ? 'Este ticket no tiene cliente asociado.' : 'This ticket has no client.'); return; }
    setUploading(true);
    try {
      const { uploadDocument } = await import('../api.js');
      const created = await uploadDocument(file, { clientId: ticket.clientId, category: 'other' });
      setDocs((prev) => (prev.some((d) => d.id === created.id) ? prev : [created, ...prev]));
      setSel((s) => ({ ...s, [created.id]: 'attach' }));
      toast.success(es ? 'Archivo adjuntado ✓' : 'File attached ✓');
    } catch (e) {
      toast.error((es ? 'No se pudo subir el archivo: ' : 'Upload failed: ') + (e?.message || ''));
    } finally { setUploading(false); }
  };

  const send = async (acknowledgePhi = false) => {
    const wantEmail = hasEmail && !!subject.trim() && !!body.trim();
    const wantSms = smsAvailable && alsoSms && !!smsText.trim();
    if (!wantEmail && !wantSms) { toast.error(es ? 'Nada que enviar' : 'Nothing to send'); return; }
    setBusy(true);
    try {
      const res = await onNotifyClient(ticket.id, {
        subject: wantEmail ? subject : '',
        body: wantEmail ? body : '',
        docs: Object.entries(sel).map(([documentId, mode]) => ({ documentId, mode })),
        lang: emailEs ? 'es' : 'en',
        acknowledgePhi,
        smsBody: wantSms ? smsText : '',
        sendSms: wantSms,
      });
      const both = wantEmail && wantSms;
      toast.success(both ? (es ? 'Correo y texto enviados ✓' : 'Email & text sent ✓')
        : wantSms ? (es ? 'Texto enviado al cliente ✓' : 'Text sent to client ✓')
        : (es ? 'Correo enviado al cliente ✓' : 'Email sent to client ✓'));
      if (res?.smsWarning) toast.error(res.smsWarning);
      onClose && onClose();
    } catch (e) {
      if (e.code === 'phi') {
        const ok = await confirmDialog({ title: es ? 'Posible info sensible' : 'Possible sensitive info', message: es ? 'Se detectó posible información sensible (PHI) en el correo. ¿Enviar de todos modos?' : 'Possible sensitive info (PHI) detected. Send anyway?', danger: true, confirmLabel: es ? 'Enviar' : 'Send' });
        if (ok) return send(true);
      } else {
        toast.error((es ? 'No se pudo enviar: ' : 'Failed to send: ') + e.message);
      }
    } finally { setBusy(false); }
  };

  const title = kind === 'resolve' ? (es ? 'Avisar al cliente — resuelto' : 'Email client — resolved') : (es ? 'Avisar al cliente' : 'Email client');
  return (
    <Modal title={title} onClose={onClose} wide>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '.8rem' }}>
        <div style={{ display: 'flex', gap: '.5rem', alignItems: 'center', fontSize: '.76rem', color: 'var(--c-text2,#7aa3c0)' }}>
          <span style={{ display: 'inline-flex', gap: 10, flexWrap: 'wrap' }}>
            {hasEmail && <span>✉️ {client.email}</span>}
            {smsAvailable && <span>📱 {client.phone}</span>}
            {!hasEmail && !smsAvailable && <span>{ticket.clientName}</span>}
          </span>
          <span style={{ marginLeft: 'auto', display: 'inline-flex', gap: 6, alignItems: 'center' }}>
            {hasEmail && <AiDraftButton lang={emailEs ? 'es' : 'en'} compact
              contextHint={`This is a customer-service email to an insurance client about their case/ticket "${ticket.subject || ''}". Keep it short, warm and reassuring. Address the reader with {{cliente}} or their name.`}
              onResult={({ subject: s, body: b }) => { if (s) setSubject(s); if (b) setBody(b); }} />}
            <button type="button" className={'dkt-btn xs ' + (emailEs ? 'primary' : 'ghost')} onClick={() => relang(true)}>🇪🇸 ES</button>
            <button type="button" className={'dkt-btn xs ' + (!emailEs ? 'primary' : 'ghost')} onClick={() => relang(false)}>🇺🇸 EN</button>
          </span>
        </div>
        {hasEmail && (<>
        <div><label className="dkt-input-lbl">{es ? 'Asunto' : 'Subject'}</label><input className="dkt-inp" value={subject} onChange={(e) => setSubject(e.target.value)} /></div>
        <div><label className="dkt-input-lbl">{es ? 'Mensaje' : 'Message'}</label><textarea className="dkt-inp" rows={8} value={body} onChange={(e) => setBody(e.target.value)} /></div>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '.5rem', marginBottom: '.35rem' }}>
            <label className="dkt-input-lbl" style={{ margin: 0 }}>{es ? 'Adjuntar documentos del cliente' : 'Attach client documents'}</label>
            <input ref={fileRef} type="file" style={{ display: 'none' }} onChange={(e) => { const f = e.target.files && e.target.files[0]; e.target.value = ''; if (f) attachLocalFile(f); }} />
            <button type="button" className="dkt-btn xs ghost" style={{ marginLeft: 'auto' }} disabled={uploading} onClick={() => fileRef.current && fileRef.current.click()}>
              <Paperclip size={12} /> {uploading ? (es ? 'Subiendo…' : 'Uploading…') : (es ? 'Subir archivo de mi PC' : 'Upload from my PC')}
            </button>
          </div>
          {docsLoading ? <div style={{ fontSize: '.76rem', color: 'var(--c-text3,#45688a)' }}>{es ? 'Cargando…' : 'Loading…'}</div>
            : docs.length === 0 ? <div style={{ fontSize: '.76rem', color: 'var(--c-text3,#45688a)' }}>{es ? 'Este cliente no tiene documentos.' : 'No documents for this client.'}</div>
            : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '.35rem', maxHeight: 200, overflowY: 'auto', border: '1px solid var(--c-border,#1a3550)', borderRadius: 8, padding: '.5rem' }}>
                {docs.map((d) => (
                  <div key={d.id} style={{ display: 'flex', alignItems: 'center', gap: '.5rem', fontSize: '.78rem' }}>
                    <input type="checkbox" checked={!!sel[d.id]} onChange={() => toggleDoc(d.id)} />
                    <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: 'var(--c-text,#c8dff0)' }}>{d.name}</span>
                    {sel[d.id] && (
                      <span style={{ display: 'inline-flex', gap: 3 }}>
                        <button type="button" title={es ? 'Enlace seguro (7 días)' : 'Secure link (7 days)'} className={'dkt-btn xs ' + (sel[d.id] === 'link' ? 'primary' : 'ghost')} onClick={() => setMode(d.id, 'link')}><Link2 size={12} /> {es ? 'Enlace' : 'Link'}</button>
                        <button type="button" title={es ? 'Adjuntar archivo real' : 'Attach real file'} className={'dkt-btn xs ' + (sel[d.id] === 'attach' ? 'primary' : 'ghost')} onClick={() => setMode(d.id, 'attach')}><Paperclip size={12} /> {es ? 'Archivo' : 'File'}</button>
                      </span>
                    )}
                  </div>
                ))}
              </div>
            )}
          <div style={{ fontSize: '.68rem', color: 'var(--c-text3,#45688a)', marginTop: '.35rem' }}>{es ? 'Enlace = descarga segura por 7 días. Archivo = adjunto real (máx. 4 MB c/u; si pesa más se envía como enlace).' : 'Link = secure 7-day download. File = real attachment (max 4 MB each; larger falls back to a link).'}</div>
        </div>
        </>)}

        {/* Text (SMS) channel — short message from the agency's connected phone. */}
        {smsAvailable && (
          <div style={{ border: '1px solid var(--c-border,#1a3550)', borderRadius: 8, padding: '.6rem', background: alsoSms ? 'var(--c-surface2,#112538)' : 'transparent' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '.5rem', fontWeight: 700, fontSize: '.8rem', color: 'var(--c-white,#eef6ff)', cursor: 'pointer' }}>
              <input type="checkbox" checked={alsoSms} onChange={(e) => setAlsoSms(e.target.checked)} /> 📱 {hasEmail ? (es ? 'Enviar también por texto (SMS)' : 'Also send by text (SMS)') : (es ? 'Enviar por texto (SMS)' : 'Send by text (SMS)')}
            </label>
            {alsoSms && (
              <div style={{ marginTop: '.5rem' }}>
                <div className="dkt-input-lbl" style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span>{es ? 'Mensaje de texto' : 'Text message'}</span>
                  <span style={{ color: smsText.length > 160 ? 'var(--c-amber,#ffb700)' : 'var(--c-text3,#45688a)', fontFamily: 'var(--font-mono)' }}>{smsText.length}/160</span>
                </div>
                <textarea className="dkt-inp" rows={3} value={smsText} onChange={(e) => setSmsText(e.target.value)} />
                <div style={{ fontSize: '.66rem', color: 'var(--c-text3,#45688a)', marginTop: '.3rem' }}>{es ? 'Corto y sin información sensible. Sale desde tu teléfono conectado.' : 'Keep it short and free of sensitive info. Sent from your connected phone.'}</div>
              </div>
            )}
          </div>
        )}

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '.5rem', alignItems: 'center' }}>
          <span style={{ fontSize: '.68rem', color: 'var(--c-text3,#45688a)', marginRight: 'auto' }}>{es ? 'Sale a nombre del agente de la agencia.' : 'Sent in the agency agent\'s name.'}</span>
          <button type="button" className="dkt-btn ghost" onClick={onClose}>{es ? 'Cancelar' : 'Cancel'}</button>
          <button type="button" className="dkt-btn primary" disabled={busy} onClick={() => send(false)}><Send size={14} /> {busy ? (es ? 'Enviando…' : 'Sending…') : (es ? 'Enviar' : 'Send')}</button>
        </div>
      </div>
    </Modal>
  );
}

// Bilingual template editor (subject + body, ES/EN) with an enable checkbox.
// The ticket templates use single-brace tokens ({cliente}, {numero}, …); the
// chips below insert them at the caret instead of the old copy/paste hint.
const TICKET_VARS = ['{cliente}', '{numero}', '{asunto}', '{tipo}', '{agente}', '{agencia}', '{resolucion}'];
function TplEditor({ es, value, onChange, label }) {
  const set = (k, v) => onChange({ ...value, [k]: v });
  const varIns = useVarInserter();
  return (
    <div style={{ border: '1px solid var(--c-border,#1a3550)', borderRadius: 8, padding: '.6rem' }}>
      <label style={{ display: 'flex', alignItems: 'center', gap: '.5rem', fontWeight: 700, fontSize: '.82rem', color: 'var(--c-white,#eef6ff)', marginBottom: '.2rem', cursor: 'pointer' }}>
        <input type="checkbox" checked={!!value.enabled} onChange={(e) => set('enabled', e.target.checked)} /> {label}
      </label>
      <div style={{ display: 'flex', alignItems: 'center', gap: '.5rem', flexWrap: 'wrap', marginBottom: '.5rem' }}>
        <VarChips vars={TICKET_VARS} inserter={varIns} lang={es ? 'es' : 'en'} style={{ flex: 1 }} />
        <AiDraftButton lang={es ? 'es' : 'en'} compact
          contextHint="This is a customer-service ticket email template to a client. Keep it short and reassuring. You may reference the case with the token {numero} (single brace)."
          onResult={({ subject, body, lang: dl }) => { if (dl === 'es') { set('subjectEs', subject); set('bodyEs', body); } else { set('subjectEn', subject); set('bodyEn', body); } }} />
      </div>
      <div style={{ opacity: value.enabled ? 1 : 0.55 }}>
        <div style={{ fontSize: '.66rem', color: 'var(--c-text3,#45688a)', fontWeight: 700, marginBottom: '.25rem' }}>✉️ {es ? 'Correo' : 'Email'}</div>
        {/* Quien guardo su plantilla antes del 2026-08-20 la tiene terminada en
            "Gracias, {agente}", y su texto NO se toca al cambiar como se
            presenta el correo. Ahora la firma del perfil se engancha sola al
            final, asi que esa despedida escrita a mano sale repetida. El aviso
            va aqui, encima del recuadro, porque es el unico sitio donde se ve
            justo cuando hace falta. */}
        <div style={{ fontSize: '.68rem', color: 'var(--c-text3,#45688a)', marginBottom: '.45rem', lineHeight: 1.5 }}>
          {es
            ? 'No hace falta que firmes: tu firma del perfil (nombre, cargo, teléfono, licencia) se añade sola al final. Si tu texto todavía termina con tu nombre, bórralo o saldrá dos veces.'
            : "No need to sign off: your profile signature (name, title, phone, licence) is added automatically at the end. If your text still ends with your name, remove it or it will appear twice."}
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '.5rem' }}>
          <div>
            <div className="dkt-cyan-lbl" style={{ fontSize: '.66rem' }}>🇪🇸 Español</div>
            <input className="dkt-inp" style={{ marginBottom: '.35rem' }} placeholder={es ? 'Asunto' : 'Subject'} value={value.subjectEs || ''} onChange={(e) => set('subjectEs', e.target.value)} {...varIns.bind(() => value.subjectEs || '', (v) => set('subjectEs', v))} />
            <textarea className="dkt-inp" rows={5} value={value.bodyEs || ''} onChange={(e) => set('bodyEs', e.target.value)} {...varIns.bind(() => value.bodyEs || '', (v) => set('bodyEs', v))} />
          </div>
          <div>
            <div className="dkt-cyan-lbl" style={{ fontSize: '.66rem' }}>🇺🇸 English</div>
            <input className="dkt-inp" style={{ marginBottom: '.35rem' }} placeholder={es ? 'Asunto' : 'Subject'} value={value.subjectEn || ''} onChange={(e) => set('subjectEn', e.target.value)} {...varIns.bind(() => value.subjectEn || '', (v) => set('subjectEn', v))} />
            <textarea className="dkt-inp" rows={5} value={value.bodyEn || ''} onChange={(e) => set('bodyEn', e.target.value)} {...varIns.bind(() => value.bodyEn || '', (v) => set('bodyEn', v))} />
          </div>
        </div>
      </div>

      {/* Text (SMS) channel — same notification, short message. Independent toggle. */}
      <div style={{ marginTop: '.7rem', paddingTop: '.6rem', borderTop: '1px dashed var(--c-border,#1a3550)' }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: '.5rem', fontWeight: 700, fontSize: '.78rem', color: 'var(--c-white,#eef6ff)', marginBottom: '.35rem', cursor: 'pointer' }}>
          <input type="checkbox" checked={!!value.smsEnabled} onChange={(e) => set('smsEnabled', e.target.checked)} /> 📱 {es ? 'También enviar por texto (SMS)' : 'Also send by text (SMS)'}
        </label>
        <div style={{ fontSize: '.68rem', color: 'var(--c-text3,#45688a)', marginBottom: '.45rem' }}>{es ? 'Mensaje corto que sale desde tu teléfono conectado. No incluyas información sensible en textos.' : 'Short message from your connected phone. Do not include sensitive info in texts.'}</div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '.5rem', opacity: value.smsEnabled ? 1 : 0.55 }}>
          {[['smsEs', '🇪🇸 Español', 'es'], ['smsEn', '🇺🇸 English', 'en']].map(([k, lbl, lc]) => {
            const cur = value[k] || '';
            return (
              <div key={k}>
                <div className="dkt-cyan-lbl" style={{ fontSize: '.66rem', display: 'flex', justifyContent: 'space-between' }}>
                  <span>{lbl}</span>
                  <span style={{ color: cur.length > 160 ? 'var(--c-amber,#ffb700)' : 'var(--c-text3,#45688a)', fontFamily: 'var(--font-mono)' }}>{cur.length}/160</span>
                </div>
                <textarea className="dkt-inp" rows={3} placeholder={TICKET_SMS_DEFAULTS.open[lc]} value={cur} onChange={(e) => set(k, e.target.value)} {...varIns.bind(() => value[k] || '', (v) => set(k, v))} />
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function CustomizeModal({ lang, onClose, customStatuses = [], customTypes = [], onCustomStatusesChange, onCustomTypesChange, emailOnOpen = {}, emailOnResolve = {}, onEmailOnOpenChange, onEmailOnResolveChange }) {
  const es = lang === 'es';
  const [statuses, setStatuses] = useState(() => (customStatuses || []).map((s) => ({ ...s })));
  const [types, setTypes] = useState(() => (customTypes || []).map((t) => (typeof t === 'string' ? { id: rid('tt_'), label: t } : { ...t })));
  const mkOpen = () => ({ ...TICKET_EMAIL_DEFAULTS.open, smsEs: TICKET_SMS_DEFAULTS.open.es, smsEn: TICKET_SMS_DEFAULTS.open.en, enabled: false, smsEnabled: false, ...(emailOnOpen || {}) });
  const mkResolve = () => ({ ...TICKET_EMAIL_DEFAULTS.resolve, smsEs: TICKET_SMS_DEFAULTS.resolve.es, smsEn: TICKET_SMS_DEFAULTS.resolve.en, enabled: false, smsEnabled: false, ...(emailOnResolve || {}) });
  const [eOpen, setEOpen] = useState(mkOpen);
  const [eResolve, setEResolve] = useState(mkResolve);
  // What the modal opened with. Saving must not push the client-notification
  // blobs unless the user actually touched them: ticketEmailOnOpen /
  // ticketEmailOnResolve are the only agent-only keys in this modal (statuses
  // and types are seat-writable), so writing them unconditionally made a SEAT
  // who merely renamed a status hit the agent-or-above PUT /agency-settings and
  // get "no tienes permiso para editarla" — while the rename itself had saved.
  const opened = useRef(null);
  if (!opened.current) opened.current = { open: JSON.stringify(mkOpen()), resolve: JSON.stringify(mkResolve()) };
  const addStatus = () => setStatuses((p) => [...p, { id: rid('ts_'), label: '', color: '#00d4ff' }]);
  const addType = () => setTypes((p) => [...p, { id: rid('tt_'), label: '' }]);
  const save = () => {
    if (onCustomStatusesChange) onCustomStatusesChange(statuses.filter((s) => s.label && s.label.trim()));
    if (onCustomTypesChange) onCustomTypesChange(types.filter((t) => t.label && t.label.trim()));
    if (onEmailOnOpenChange && JSON.stringify(eOpen) !== opened.current.open) onEmailOnOpenChange(eOpen);
    if (onEmailOnResolveChange && JSON.stringify(eResolve) !== opened.current.resolve) onEmailOnResolveChange(eResolve);
    onClose && onClose();
  };
  return (
    <Modal title={es ? 'Personalizar tickets' : 'Customize tickets'} onClose={onClose}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
        <div>
          <div className="dkt-cyan-lbl" style={{ marginBottom: '.4rem' }}>{es ? 'Estados personalizados (con color)' : 'Custom statuses (with color)'}</div>
          <div style={{ fontSize: '.72rem', color: 'var(--c-text3,#45688a)', marginBottom: '.45rem' }}>{es ? 'Se suman a los fijos (Abierto, En proceso, En espera, Resuelto, Cerrado).' : 'Added to the built-ins.'}</div>
          {statuses.map((s, i) => (
            <div key={s.id} style={{ display: 'flex', gap: '.5rem', alignItems: 'center', marginBottom: '.4rem' }}>
              <input type="color" value={s.color || '#00d4ff'} onChange={(e) => setStatuses((p) => p.map((x, j) => j === i ? { ...x, color: e.target.value } : x))} style={{ width: 32, height: 32, border: '1px solid var(--c-border,#1a3550)', borderRadius: 7, background: 'none', cursor: 'pointer' }} />
              <input className="dkt-inp" style={{ flex: 1 }} placeholder={es ? 'Nombre del estado' : 'Status name'} value={s.label} onChange={(e) => setStatuses((p) => p.map((x, j) => j === i ? { ...x, label: e.target.value } : x))} />
              <button type="button" onClick={() => setStatuses((p) => p.filter((_, j) => j !== i))} style={{ background: 'none', border: 'none', color: 'var(--c-red,#ff3d6b)', cursor: 'pointer' }}><X size={16} /></button>
            </div>
          ))}
          <button type="button" className="dkt-btn ghost xs" onClick={addStatus}><Plus size={13} /> {es ? 'Agregar estado' : 'Add status'}</button>
        </div>
        <div>
          <div className="dkt-cyan-lbl" style={{ marginBottom: '.4rem' }}>{es ? 'Tipos de solicitud' : 'Request types'}</div>
          {types.map((t, i) => (
            <div key={t.id} style={{ display: 'flex', gap: '.5rem', alignItems: 'center', marginBottom: '.4rem' }}>
              <input className="dkt-inp" style={{ flex: 1 }} placeholder={es ? 'Nombre del tipo' : 'Type name'} value={t.label} onChange={(e) => setTypes((p) => p.map((x, j) => j === i ? { ...x, label: e.target.value } : x))} />
              <button type="button" onClick={() => setTypes((p) => p.filter((_, j) => j !== i))} style={{ background: 'none', border: 'none', color: 'var(--c-red,#ff3d6b)', cursor: 'pointer' }}><X size={16} /></button>
            </div>
          ))}
          <button type="button" className="dkt-btn ghost xs" onClick={addType}><Plus size={13} /> {es ? 'Agregar tipo' : 'Add type'}</button>
        </div>
        <div>
          <div className="dkt-cyan-lbl" style={{ marginBottom: '.4rem' }}>{es ? 'Avisos automáticos al cliente — correo y texto (opcional)' : 'Automatic client notifications — email & text (optional)'}</div>
          <div style={{ fontSize: '.72rem', color: 'var(--c-text3,#45688a)', marginBottom: '.5rem' }}>{es ? 'Apagados por defecto. El acuse al abrir sale automático; el de cierre siempre te pide confirmar antes de enviar. Puedes prender correo, texto (SMS) o ambos. Todo sale a nombre del agente.' : 'Off by default. The acknowledgement on open goes out automatically; the resolution one always asks you to confirm first. You can enable email, text (SMS) or both. Everything is sent in the agent\'s name.'}</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '.6rem' }}>
            <TplEditor es={es} value={eOpen} onChange={setEOpen} label={es ? 'Acuse automático al abrir un ticket' : 'Auto-acknowledgement when a ticket opens'} />
            <TplEditor es={es} value={eResolve} onChange={setEResolve} label={es ? 'Aviso de cierre (al marcar Resuelto/Cerrado)' : 'Resolution notice (on Resolved/Closed)'} />
          </div>
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '.5rem' }}>
          <button type="button" className="dkt-btn ghost" onClick={onClose}>{es ? 'Cancelar' : 'Cancel'}</button>
          <button type="button" className="dkt-btn primary" onClick={save}>{es ? 'Guardar' : 'Save'}</button>
        </div>
      </div>
    </Modal>
  );
}
